// What? Hook for the V2 full Document Type export/import (zip) feature.
// Why? Export a whole Document Type (all fields + template files) as a .zip and
//      import one back into a workspace. Separate from the V1 structure-only export.
// How? Export downloads a blob from the backend; import POSTs the zip as multipart.
import { useCallback, useState } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";

const BASE = "/api/workspace/meridian/admin/document_types";

export interface ImportedDocumentType {
  DocumentTypeID: number;
  Name: string;
  [key: string]: unknown;
}

const extractFilename = (response: Response): string | null => {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return null;
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return match ? match[1] : null;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Turn a raw import failure into a friendly, user-facing message. We never show
// the HTTP status line or the technical validation detail (e.g. "missing
// document-type.json") to end users.
const friendlyImportMessage = (error: any): string => {
  const detail = String(error?.response?.detail ?? "");
  // The template-count limit message is already user-appropriate; keep it.
  if (/exceeds the limit/i.test(detail)) return detail;
  // Any other 400 means the archive is not a usable Document Type export.
  if (error?.status === 400) {
    return "That file is not a valid Document Type export. Please upload a .zip created with the Export Document Type button.";
  }
  return "Something went wrong while importing this document type. Please try again.";
};

export const useDocumentTypePortability = (workspace_id?: string) => {
  const authFetch = useAuthFetch();
  const authFetchBlob = useAuthFetch("blob");
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const exportDocumentType = useCallback(
    async (documentTypeId: number) => {
      if (!workspace_id) return;
      setIsExporting(true);
      try {
        const response: Response = await authFetchBlob(
          `${BASE}/${documentTypeId}/export?workspace_id=${workspace_id}`,
          "export document type",
        );
        const blob = await response.blob();
        const filename =
          extractFilename(response) ?? `document-type-${documentTypeId}.zip`;
        triggerDownload(blob, filename);
      } finally {
        setIsExporting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
    [workspace_id],
  );

  const importDocumentType = useCallback(
    async (file: File): Promise<ImportedDocumentType> => {
      if (!workspace_id) {
        throw new Error(
          "Something went wrong while importing this document type. Please try again.",
        );
      }
      setIsImporting(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        // Tag contains "fetch" so useAuthFetch suppresses its generic error
        // toast; we surface a friendly inline message from the caller instead.
        const result = await authFetch(
          `${BASE}/import?workspace_id=${workspace_id}`,
          "fetch import document type",
          {
            method: "POST",
            body: formData,
          },
          300000,
        );
        return result as ImportedDocumentType;
      } catch (error) {
        throw new Error(friendlyImportMessage(error));
      } finally {
        setIsImporting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
    [workspace_id],
  );

  return { exportDocumentType, importDocumentType, isExporting, isImporting };
};
